'use client'

import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/lib/stores/auth-store'

/**
 * Hook to automatically extend user sessions on activity
 * Extends session every 10 minutes if user is active
 */
export function useSessionExtension() {
    const extendSession = useAuthStore((state) => state.extendSession)
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated())
    const lastActivityRef = useRef<number>(Date.now())
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        if (!isAuthenticated) {
            // Clear interval if user is not authenticated
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
            return
        }

        // Track user activity
        const updateActivity = () => {
            lastActivityRef.current = Date.now()
        }

        // Activity events to track
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

        // Add event listeners for user activity
        events.forEach(event => {
            document.addEventListener(event, updateActivity, { passive: true })
        })

        // Set up interval to check and extend session
        intervalRef.current = setInterval(() => {
            const now = Date.now()
            const timeSinceActivity = now - lastActivityRef.current
            const tenMinutes = 10 * 60 * 1000 // 10 minutes in milliseconds

            // If user was active in the last 10 minutes, extend session
            if (timeSinceActivity < tenMinutes) {
                extendSession()
            } else {
            }
        }, 10 * 60 * 1000) // Check every 10 minutes

        // Cleanup function
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, updateActivity)
            })
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [isAuthenticated, extendSession])

    return null // This hook doesn't render anything
}